/**
 * Utilitários para tratamento de datas no fuso horário brasileiro
 */

/**
 * Interpreta "YYYY-MM-DD" como data civil no fuso local (nunca use new Date("YYYY-MM-DD") — isso é UTC meia-noite).
 */
export const parseLocalYmdToDate = (ymd: string): Date | null => {
  if (!ymd || typeof ymd !== "string") return null;
  const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo, d);
};

/** Formata Date (instante local) como YYYY-MM-DD civil local. */
export const formatDateToLocalYmd = (date: Date): string => {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

/**
 * Valor vindo do banco (ISO ou YYYY-MM-DD) → string YYYY-MM-DD para inputs/calendário.
 * Prefere a parte da data quando for claramente date-only nos primeiros 10 caracteres.
 */
export const storedPaymentDateToFormYmd = (stored: string | null | undefined): string => {
  if (!stored) return "";
  const head = stored.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  const dt = new Date(stored);
  if (Number.isNaN(dt.getTime())) return "";
  return formatDateToLocalYmd(dt);
};

/**
 * Converte uma data local para UTC no fuso horário brasileiro
 * @param dateString - Data no formato YYYY-MM-DD
 * @returns Data UTC como string ISO
 */
export const convertLocalDateToUTC = (dateString: string): string | null => {
  if (!dateString) return null;

  const parsed = parseLocalYmdToDate(dateString);
  if (!parsed) return null;
  // Meio-dia local evita mudança de dia ao serializar para ISO
  const utcDate = new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate(),
    12,
    0,
    0
  );
  return utcDate.toISOString();
};

/**
 * Converte uma data UTC para o fuso horário brasileiro
 * @param utcDateString - Data UTC como string ISO
 * @returns Data formatada no fuso horário brasileiro
 */
export const convertUTCToBrazilianDate = (utcDateString: string): string => {
  if (!utcDateString) return '';
  
  const date = new Date(utcDateString);
  // Ajustar para fuso horário brasileiro (UTC-3)
  const brazilianDate = new Date(date.getTime() + (3 * 60 * 60 * 1000));
  return brazilianDate.toLocaleDateString('pt-BR');
};

/**
 * Formata uma data de criação (sem ajuste de timezone)
 * @param dateString - Data como string ISO
 * @returns Data formatada no formato brasileiro
 */
export const formatCreationDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  } catch (error) {
    console.error('Erro ao formatar data de criação:', error);
    return dateString;
  }
};

/**
 * Converte uma data UTC para o fuso horário brasileiro com hora
 * @param utcDateString - Data UTC como string ISO
 * @returns Data e hora formatada no fuso horário brasileiro
 */
export const convertUTCToBrazilianDateTime = (utcDateString: string): string => {
  if (!utcDateString) return '';
  
  const date = new Date(utcDateString);
  // Ajustar para fuso horário brasileiro (UTC-3)
  const brazilianDate = new Date(date.getTime() + (3 * 60 * 60 * 1000));
  return brazilianDate.toLocaleString('pt-BR');
};

/**
 * Verifica se uma data está vencida
 * @param paymentDate - Data de pagamento
 * @returns Objeto com informações sobre vencimento
 */
export const checkOverdueStatus = (paymentDate: string) => {
  if (!paymentDate) return { isOverdue: false, overdueDays: 0 };

  const today = new Date();
  const head = paymentDate.slice(0, 10);
  const fromYmd = /^\d{4}-\d{2}-\d{2}$/.test(head) ? parseLocalYmdToDate(head) : null;
  const paymentDateObj =
    fromYmd && !Number.isNaN(fromYmd.getTime()) ? fromYmd : new Date(paymentDate);
  if (Number.isNaN(paymentDateObj.getTime())) return { isOverdue: false, overdueDays: 0 };

  // Comparar apenas ano, mês e dia (civil local)
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const paymentDateOnly = new Date(
    paymentDateObj.getFullYear(),
    paymentDateObj.getMonth(),
    paymentDateObj.getDate()
  );
  
  const isOverdue = todayDate > paymentDateOnly;
  const overdueDays = isOverdue ? 
    Math.floor((todayDate.getTime() - paymentDateOnly.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  
  return { isOverdue, overdueDays };
};

/**
 * Formata uma data para exibição no formato brasileiro
 * @param dateString - Data como string
 * @returns Data formatada
 */
export const formatBrazilianDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return dateString;
  }
};

/**
 * Obtém a data atual no fuso horário brasileiro
 * @returns Data atual formatada
 */
export const getCurrentBrazilianDate = (): string => {
  const now = new Date();
  const brazilianDate = new Date(now.getTime() + (3 * 60 * 60 * 1000));
  return brazilianDate.toISOString().split('T')[0];
}; 